/**
 * @format
 * @module lib/election/party-leaders
 */
import tgamConfig from "config/tgam";
import { getProp, parseDataSetObject, qs, qsa } from "utils/utils";
import ee from "lib/eventemitter";
import { getEntriesByName, mark, measure } from "lib/performance/user-timing";
import logger from "utils/logger";
import { compareResults, getStatus } from "lib/election/lead-data-helpers";
import escape from "lodash/escape";
import { dialogs, getDialogById } from "ui/dialog-manager";
import { populateRidingDialog } from "lib/election/riding-cards";
import { getPartyLeaderItem } from "ui/templates/election/party-leader-item";
import { errorStateHideFeature, errorStateShowFeature } from "lib/election/error-state";

const debugTag = "[ELECTION-PARTY-LEADERS]";

// One of 2019, 2021.
let electionYear = getProp(tgamConfig, "meta.electionYear", "2021");

const partyMappings = {
  LIB: {
    name: "Liberal Party",
    modifier: "lib"
  },
  CON: {
    name: "Conservative Party",
    modifier: "cpc"
  },
  NDP: {
    name: "New Democratic Party",
    modifier: "ndp"
  },
  BQ: {
    name: "Bloc Québécois",
    modifier: "bq"
  },
  GRN: {
    name: "Green Party",
    modifier: "gp"
  },
  PP: {
    name: "People's Party of Canada",
    modifier: "ppc"
  },
  PPC: {
    name: "People's Party of Canada",
    modifier: "ppc"
  }
};

const dialogId = "js-modal-dialog-election-riding";

let container = null; // Party leaders feature container

let partiesData = {
  "2019": [
    {
      party: "LIB",
      rid: 24055, // riding id - Papineau
      ridingName: "Papineau",
      id: "9" // candidate id (within above riding) - Justin Trudeau
    },
    {
      party: "CON",
      rid: 47008, // Regina--Qu'appelle
      ridingName: "Regina--Qu'appelle",
      id: "6" // Andrew Sheer
    },
    {
      party: "NDP",
      rid: 59003, // Burnaby South
      ridingName: "Burnaby South",
      id: "5" // Jagmeet Singh
    },
    {
      party: "BQ",
      rid: 24011, // Beloeil-Chambly
      ridingName: "Beloeil-Chambly",
      id: "2" // Yves-Francois Blanchet
    },
    {
      party: "GRN",
      rid: 59027, // Saanich-Gulf Islands
      ridingName: "Saanich-Gulf Islands",
      id: "3" // Elizabeth May
    },
    {
      party: "PPC",
      rid: 24007, // Beauce
      ridingName: "Beauce",
      id: "1" // Maxime Bernier
    }
  ],
  "2021": [
    {
      party: "LIB",
      rid: 24055,
      ridingName: "Papineau",
      id: "8" // candidate id (within above riding) - Justin Trudeau
    },
    {
      party: "CON",
      rid: 35023,
      ridingName: "Durham",
      id: "5" // Erin O'Toole
    },
    {
      party: "NDP",
      rid: 59003,
      ridingName: "Burnaby South",
      id: "5" // Jagmeet Singh
    },
    {
      party: "BQ",
      rid: 24011,
      ridingName: "Beloeil-Chambly",
      id: "2" // Yves-Francois Blanchet
    },
    {
      party: "GRN",
      rid: 35108,
      ridingName: "Toronto Centre",
      id: "6" // Annamie Paul
    },
    {
      party: "PPC",
      rid: 24007, // Beauce
      ridingName: "Beauce",
      id: "1" // Maxime Bernier
    }
  ]
};

/**
 * @method init
 * @description Initialize the party leaders component.
 */
export function initElectionPartyLeaders() {
  let invokingButton = ""; // riding link that is clicked to open riding modal
  container = qs(".js-election-party-leaders");

  if (!container) {
    return;
  }

  ee.on("election:leading-data-failed", () => {
    errorStateHideFeature(container);
  });

  ee.on("election:all-data", (leadingData, ridingDataObject, ridingDataArray) => {
    if (!leadingData && !ridingDataObject && !ridingDataArray) {
      logger.info(`${debugTag} Null data`);
      return;
    }

    logger.info(`${debugTag} Leading Data`, leadingData);
    logger.info(`${debugTag} Riding Data Object`, ridingDataObject);
    logger.info(`${debugTag} Riding Data Array`, ridingDataArray);

    mark("party-leaders_started");

    errorStateShowFeature(container);

    let winningParty = "";
    const status = getStatus(leadingData);

    if (status === "complete") {
      // Make a copy of the array since we'll be adding an element to it for testing purposes.
      // We don't want to actually alter "data".
      let leadingElectedPartyData = [...getProp(leadingData, "Leading.Party", [])];

      // Sort the data from highest to lowest number of seats.
      leadingElectedPartyData.sort((resultA, resultB) => compareResults(resultA, resultB));

      if (leadingElectedPartyData && leadingElectedPartyData.length > 0) {
        winningParty = getProp(leadingElectedPartyData[0], "Name", "");
      }

      logger.info(`${debugTag} leadingElectedPartyData `, leadingElectedPartyData);
      logger.info(`${debugTag} Winning Party `, winningParty);
    }

    // For testing purposes (with mock data)
    // if (/partyLeadersTest=true/im.test(window.location.search)) {
    //   import("lib/election/mock-data/party-leaders-test-data-complete" /* webpackChunkName: "election-party-leaders-test-data-complete" */)
    //     .then(({ testRidingsData, testPartiesData, testWinningParty, testStatus }) => {
    //       // Override the data
    //       logger.info(`${debugTag} testRidingsData`, testRidingsData);
    //       logger.info(`${debugTag} testPartiesData`, testPartiesData);
    //       logger.info(`${debugTag} testWinningParty`, testWinningParty);
    //       logger.info(`${debugTag} testStatus`, testStatus);

    //       if (testRidingsData && testPartiesData && testWinningParty && testStatus) {
    //         logger.info(`${debugTag} Using mock complete status data`, ridingDataObject, partiesData, winningParty, status);

    //         populatePartyLeaders(testRidingsData, testPartiesData, testWinningParty, testStatus);
    //       }
    //     }).catch(console.error);
    // } else { // In non-testing mode.  Use data from API.
    //   populatePartyLeaders(ridingDataObject, partiesData, winningParty, status);
    // }

    populatePartyLeaders(ridingDataObject, partiesData[electionYear], winningParty, status);

    handleDialog();

    mark("party-leaders_done");
    measure("party-leaders");
    logger.info(`${debugTag} Performance measure result:`, getEntriesByName("party-leaders"));
  });

  ee.on("tgam-modal-dialog:closing", dialog => {
    if (dialog.getRootElement().id === dialogId) {
      invokingButton = dialog.getOpenButtonThatSpawnedDialog();

      logger.info(`${debugTag} invokingButton`, invokingButton);
    }
  });

  ee.on("tgam-modal-dialog:closed", dialog => {
    if (dialog.getRootElement().id === dialogId) {
      if (invokingButton && invokingButton.classList.contains("c-election-party-leaders__riding")) {
        const parentContainer = invokingButton.parentElement;
        logger.info(`${debugTag} parent`, parentContainer);

        if (parentContainer) {
          parentContainer.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  });
}

/**
 * From all the candidates within a single riding, find a single candidate, by candidate id.
 * @param {Array<Object>} candidates - Array of JSON objects representing candidates from a particular riding and their election results
 * @param {String} candidateId - candidate's ID
 * @returns {Object} candidate matching candidateId
 */
function findCandidate(candidates, candidateId) {
  // This function is based on but refactored/simplified from http://admin.theglobeandmail.com/interactives/prototypes/riding_results_map/scripts/main.js.

  return candidates.find(candidate => {
    // 2019 and 2021 feed data differ in data values (String vs. Number).
    // Let's just convert them all to numbers so we have comparison of the same type.
    return Number(candidate.ID) === Number(candidateId);
  });
}

/**
 * Updates party leaders with leading/elected by, or behind/lost by information.
 * @param {Object} ridings - election riding data
 * @param {Array<Object>} parties - array of parties and their party leader's candidate ID and associated riding ID
 * @param {String} winningParty - winning party for the federal election (in abbreviated format), supply empty string if none at the moment
 * @param {String} status of the election. Possible values ("" for no results, "partial", "complete")
 */
function populatePartyLeaders(ridings, parties, winningParty, status) {
  // Array with party leader results with information about increase/decrease in votes and leading/behind and won/lost info.
  const partyLeaderInfoArray = [];
  const partyLeadersContent = qs(".c-election-party-leaders__content");
  const headshotURLs = parseDataSetObject(container.dataset.headshots);
  const partyLeaderPartialClass = "c-election-party-leaders--partial";
  const partyLeaderCompleteClass = "c-election-party-leaders--complete";

  logger.info(`${debugTag} headShotURLs:`, headshotURLs);

  if (!partyLeadersContent) {
    logger.error(`${debugTag} Party leader content container doesn't exist on the page`);
    return;
  }

  logger.info(`${debugTag} container classlist`, container.classList);

  if (status === "partial") {
    // Technically, there should never be the case where partial follows complete status of the election;
    // However, this may be the case during the CP test cycles.
    container.classList.remove(partyLeaderCompleteClass);
    container.classList.add(partyLeaderPartialClass);
  } else if (status === "complete") {
    // We need to account for the 30s refresh.  We don't want to append
    // additional/conflicting classes. If it doesn't exist, then the following is okay too (i.e., does nothing).
    container.classList.remove(partyLeaderPartialClass);
    container.classList.add(partyLeaderCompleteClass);
  }

  parties.forEach(party => {
    // Within the candidates in this party's lead riding, find the party leader candidate's election results, by candidate ID.
    let partyLeaderResult = findCandidate(ridings[party.rid].Cand, party.id);

    let cand1st = null; // Lead candidate in riding
    let cand2nd = null; // Second candidate in riding

    // JSON object representing the party leader's result with information about increase/decrease in votes and
    // leading/behind and won/lost info.
    let partyLeaderInfo = {
      name: `${getProp(partyLeaderResult, "FN", "")} ${getProp(partyLeaderResult, "LN", "")}`,
      headshotUrl: escape(getProp(headshotURLs, party.party, "")),
      cid: getProp(partyLeaderResult, "ID", ""), // candidate's ID,
      rid: party.rid, // candidate's riding ID'
      ridingName: party.ridingName,
      partyName: partyMappings[party.party] ? partyMappings[party.party].name : "",
      abbr: party.party,
      modifier: partyMappings[party.party] ? partyMappings[party.party].modifier : "",
      votes: Number(getProp(partyLeaderResult, "V", "0")),
      diffStatus: "", // possible values: "increase"/"decrease" (in terms of number of votes)
      blurb: "",
      elected: getProp(partyLeaderResult, "E", "No"),
      isPM: false
    };

    // Riding result for party leader's riding.
    const ridingResult = ridings[party.rid];

    // Phrase and vote logic from http://admin.theglobeandmail.com/interactives/prototypes/riding_results_map/scripts/main.js.
    let phrase = ""; // Wording before votes
    let votes = 0; // Votes after wording

    if (ridingResult) {
      // This assumes that candidates are sorted from highest to lowest votes.
      cand1st = ridingResult.Cand[0]; // Lead candidate in riding
      cand2nd = ridingResult.Cand[1]; // Second candidate in riding
    }

    if (partyLeaderInfo.cid === getProp(cand1st, "ID", "")) {
      if (partyLeaderInfo.elected === "Yes") {
        phrase = "Won by ";
        votes = partyLeaderInfo.votes - Number(getProp(cand2nd, "V", ""));
      } else {
        phrase = "Leading by ";
        votes = partyLeaderInfo.votes - Number(getProp(cand2nd, "V", ""));
      }
      partyLeaderInfo.diffStatus = "increase";
    } else {
      phrase = getProp(cand1st, "E", "No") === "Yes" ? "Lost by " : "Behind by ";
      partyLeaderInfo.diffStatus = "decrease";
      votes = Number(getProp(cand1st, "V", "")) - partyLeaderInfo.votes;
    }

    // Party leader for that party is now PM. Use that new array to populate the feature.
    logger.info(`${debugTag} Party:`, party);
    logger.info(`${debugTag} Party Leader's Riding Result:`, partyLeaderResult);
    logger.info(`${debugTag} First Candidate's Result:`, cand1st);
    logger.info(`${debugTag} Second Candidate's Result:`, cand2nd);
    logger.info(`${debugTag} Result:`, phrase, votes);

    if (Number(getProp(cand1st, "V", "")) !== 0) {
      // Format the numeric number to be comma-delimited
      let num = new Intl.NumberFormat("en-US").format(votes);

      partyLeaderInfo.blurb = phrase + num + " votes";
    }

    if (partyLeaderInfo.abbr === winningParty) {
      // Found the prime minister.
      partyLeaderInfo.isPM = true;
    }

    partyLeaderInfoArray.push(partyLeaderInfo);

    logger.info(
      `${debugTag} Election status: ${status}. Is PM? ${partyLeaderInfo.isPM}.  Current party: ${partyLeaderInfo.abbr}.  Winning party: ${winningParty}`,
      partyLeaderInfo.partyAbbr === winningParty
    );
  });

  logger.info(`${debugTag} Final party leaders array:`, partyLeaderInfoArray);

  partyLeadersContent.innerHTML = getPartyLeaderItems(partyLeaderInfoArray);
}

/**
 * Returns HTML for all party leader items representing results from partyLeaderInfoArray.
 * @param {Array<Object>} partyLeaderInfoArray
 * @returns {String} HTML for all party leader items
 */
function getPartyLeaderItems(partyLeaderInfoArray) {
  let partyLeaderItems = "";

  partyLeaderInfoArray.forEach(partyLeaderInfo => {
    partyLeaderItems += getPartyLeaderItem(partyLeaderInfo);
  });

  return partyLeaderItems;
}

/**
 * Send data to analytics
 * @param {HTMLElement} container - The root element
 * @param {String} partyLeader - Name of Party Leader
 */
function sendAnalytics(container, partyLeader) {
  ee.trigger("analytics.trackGTMEvent", [
    {
      feature: `Elections ${electionYear}`,
      type: "button",
      label: `Leader Performance: ${partyLeader}`,
      action: "Open riding view"
    },
    "clickEvent"
  ]);
}

/**
 * Handles the riding modal dialog and click event handlers for when the riding links are clicked/touched.
 */
function handleDialog() {
  const ridingDialog = getDialogById("js-modal-dialog-election-riding");

  // Fetch all of the relevant open buttons that currently exist on the page
  const openButtons = qsa(`[data-popup="${dialogId}"]`);

  logger.info(`${debugTag} ridingDialog`, ridingDialog);
  logger.info(`${debugTag} openButtons:before`, ridingDialog.openButtons);

  ridingDialog.initOpenButtons(openButtons);

  logger.info(`${debugTag} openButtons:after`, ridingDialog.openButtons);
  logger.info(`${debugTag} status`, status);

  container.addEventListener("click", event => {
    event.preventDefault();
    const item = event.target.closest(".c-election-party-leaders__riding");

    logger.info(`${debugTag} item`, item);

    if (item) {
      const ridingID = Number(item.dataset.ridingId);
      const leaderName = item.dataset.leaderName;

      logger.info(`${debugTag} leader name`, leaderName);
      logger.info(`${debugTag} dialogs`, dialogs);
      logger.info(`${debugTag} ridingDialog`, ridingDialog);

      populateRidingDialog(ridingDialog, ridingID);

      sendAnalytics(container, leaderName);
    }
  });
}
